import { computed, watch, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery } from '@tanstack/vue-query'
import {
  fetchClientPayments,
  type ClientPayment,
  type ClientPaymentsResponse,
} from '@/api/clientPayments'
import {
  fetchClientBusinessServices,
  type ClientBusinessService,
  type ClientBusinessServicesResponse,
} from '@/api/clientBusinessServices'
import {
  fetchClientBalances,
  type ClientBalance,
  type ClientBalancesResponse,
} from '@/api/clientBalances'
import {
  fetchClientDiseases,
  type ClientDisease,
  type ClientDiseasesResponse,
} from '@/api/clientDiseases'
import type { ClientSubscription } from '@/composables/useClientSubscriptions'
import { useSubscriptionTypeCatalog } from '@/composables/useSubscriptionTypeCatalog'
import { clientCardPollingInterval } from '@/config/api'
import { majorToMinor } from '@/utils/majorToMinor'

/**
 * Discriminator for the merged ledger rows rendered in the client card visits
 * table. Cell components select their rendering branch from this kind:
 *
 * - `lesson` and `payment` are normal column rows (date / label / Δ / balance).
 * - `balance-event`, `disease-event`, `subscription-event` and
 *   `payment-subscription` and `lesson-subscription` are full-row entries
 *   rendered by `EventRowCell` via `DynamicTable.rowOverride`. The last two are
 *   full-width notes placed directly under a payment that bought a subscription
 *   ("for subscription «…»") or a visit one covered ("Subscription 3/5").
 */
export type MergedRowKind =
  | 'lesson'
  | 'payment'
  | 'balance-event'
  | 'disease-event'
  | 'subscription-event'
  | 'payment-subscription'
  | 'lesson-subscription'

export interface MergedRow {
  // One row in the merged time-sorted ledger displayed in the client card visits
  // table. Built from four streams (business services, payments, monthly
  // balances, diseases) and ordered DESCENDING by `date`.

  kind: MergedRowKind
  // Drives cell rendering and the `rowOverride` decision in `DynamicTable`.

  rowId: string
  // Stable id used as the Vue `:key` and for selection comparisons. Encodes
  // the source stream and source-record id so two rows from different streams
  // never collide.

  date: string
  // ISO datetime used for sorting and rendered by the date column.

  label?: string
  // Display label for transaction rows. For payments this is the literal
  // `Payment`; for lessons this is a fallback (`Service #<id>`) used when
  // the consumer has no resolved service-type name yet. Consumers that
  // can resolve the catalogue (see `serviceTypeId` and the
  // `service_types_store`) should prefer the resolved name and fall back
  // to this string only when the store has not yet loaded. Unused for
  // full-row event rows.

  serviceTypeId?: string
  // For `lesson` rows: source `business_service_type_id` carried through
  // verbatim so the consumer can resolve a display name via
  // `useServiceTypesStore().nameForServiceTypeId(...)`. Always
  // `undefined` on payment / balance / disease rows.

  deltaMinor?: number
  // Signed money change in minor units. Negative for lessons, positive for
  // payments, omitted for full-row event rows. For lessons this is derived
  // from `price_with_discount` (the amount actually charged), not
  // `base_price` — the running balance must reflect what the client was
  // really billed.

  basePriceMinor?: number
  // For `lesson` rows only: the pre-discount price in minor units, signed
  // to match `deltaMinor`'s convention. Only set when it differs from the
  // charged price (i.e. a discount actually applied), so `SignedAmountCell`
  // can show it as a struck-through "was" line above the charged amount.
  // Undefined for rows with no discount and for non-lesson rows.

  balanceMinor?: number
  // Running balance in minor units after this row, computed forward in time
  // from a `0` seed. Each `balance-event` re-anchors the running total to
  // the server-truth snapshot, so transactions before the first snapshot
  // accumulate from zero and are corrected the moment a snapshot enters
  // the walk. Stays `undefined` only on rows that contribute no balance
  // (e.g. `disease-event`) — `lesson` / `payment` rows always carry a
  // numeric running balance.

  balanceAmountMinor?: number
  // For `balance-event` rows: the snapshot amount in minor units (drives the
  // `Balance — N` label rendered by `EventRowCell`).

  diseaseFromDate?: string
  // For `disease-event` rows: ISO start of the sick-leave range; the row's
  // primary `date` is the `to_date` so it sorts at the end of the leave.

  diseaseNotes?: string
  // For `disease-event` rows: free-form notes from the API; rendered after
  // the date range in `EventRowCell`.

  anchor?: MergedRow
  // For `payment-subscription` and `lesson-subscription` note rows: the row
  // they annotate, so a note stays glued to it when timestamps tie.

  subscriptionId?: string
  // For `lesson` rows covered by a subscription (Δ is 0 and the balance does
  // not move), and for `payment` rows that bought one.

  subscriptionTypeId?: string
  // For `payment` and `payment-subscription` rows: the catalog plan id of the
  // subscription the payment bought, so its name can be resolved for the
  // "for subscription «…»" note.

  subscriptionUsed?: number
  // For covered `lesson` rows: this visit's ordinal within its subscription
  // (the `3` of `3/5`); undefined when the quota is unknown (unlimited plan).

  subscriptionTotal?: number | null
  // Visit quota of the subscription for covered lessons and
  // `subscription-event` rows; null when unlimited.

  subscriptionEvent?: 'exhausted' | 'expired'
  // For `subscription-event` rows: the visit quota ran out, or the validity
  // period ended before it did.

  subscriptionName?: string
  // For `subscription-event` rows: plan name shown in the band.
}

export interface AlignedClientStreams {
  // Reactive surface returned by `useAlignedClientStreams`. All values are
  // already merged / sorted / aligned so the consumer only needs to render.

  rows: ComputedRef<MergedRow[]>
  // Merged time-sorted ledger rows ready for the visits `DynamicTable`.

  isInitialLoading: ComputedRef<boolean>
  // True while any of the four streams is still in its initial fetch (no
  // first page resolved yet). The card shows the loading gif during this
  // window.

  isAligning: ComputedRef<boolean>
  // True while the alignment loop is still fetching extra pages to catch a
  // laggy stream up to the others. Combined with `isInitialLoading` decides
  // whether the visits area shows the gif or the table.

  hasMore: ComputedRef<boolean>
  // True when at least one stream still has older data that `extendBack`
  // could request.

  isFetchingMore: ComputedRef<boolean>
  // True while any stream is fetching its next page (initial load or
  // alignment or user-triggered extend).

  extendBack: () => void
  // Triggers the next page of the bottleneck stream — the one whose
  // `oldestFetched` is the most recent among streams with `hasNextPage`. The
  // alignment watcher then catches the others up.
}

interface StreamMeta {
  // Compact view of one stream used by the alignment watcher and `extendBack`.
  // Bundles together the oldest-fetched date plus the controls needed to load
  // more, so the orchestration logic can iterate over `STREAMS` without
  // repeating itself for each of the four queries.

  name: string
  // Debug label only; visible in `console.log` traces while developing.

  oldest: string | undefined
  // ISO of the oldest item loaded so far for this stream, or `undefined`
  // when no items have arrived yet.

  hasNextPage: boolean
  // Whether the stream still has older data to fetch.

  isFetchingNextPage: boolean
  // Suppresses re-firing `fetchNextPage` while a request is already in flight.

  fetchNextPage: () => void
  // Fires the next page request; alignment + extendBack call this.
}

/**
 * Picks the oldest ISO datetime out of an iterable. Returns `undefined` when
 * empty. ISO 8601 strings sort lexicographically the same way they sort
 * chronologically, so the comparison can stay string-based without any Date
 * parsing in the hot path.
 */
function pickOldest(dates: Iterable<string>): string | undefined {
  let oldest: string | undefined
  for (const d of dates) {
    if (oldest === undefined || d < oldest) oldest = d
  }
  return oldest
}

/**
 * Wraps the four per-client paginated streams (`/payments/clients/{id}`,
 * `/business-services/clients/{id}`, `/balances/person/{id}`,
 * `/diseases/clients/{id}`) and exposes a single merged ledger ready for the
 * client card.
 *
 * Alignment: streams may differ wildly in items-per-page density (business
 * services are densest; balances are sparse monthly snapshots). Every time
 * one stream's `oldestFetched` moves further back than the others, a watcher
 * fetches the next page of every stream that still lags so the visible
 * window always represents the same date range.
 *
 * Refresh: each query uses `staleTime` and `refetchInterval` of one minute
 * with `refetchIntervalInBackground: false`, satisfying the "refresh on
 * mount when older than 1 min, every 1 min while active, no background
 * polling" rule from the plan.
 *
 * Permission gating: `permissions` carries one `hasRole(...)` result per
 * stream (payments → PaymentsView, services → BusinessServicesView,
 * balances → BalancesView, diseases → DiseasesView) so a user missing one
 * role still sees the other three streams merged instead of the whole card
 * failing. Each stream's own `enabled` is AND'd with its permission; see
 * `isInitialLoading` below for why that alone isn't enough — a disabled
 * TanStack Query stays `isPending: true` forever (it never fetches), so
 * naively OR-ing all four `.isPending` values would spin forever the
 * moment any one stream is permission-disabled.
 */
export function useAlignedClientStreams(
  clientId: Ref<string | null>,
  permissions: {
    payments: ComputedRef<boolean>
    services: ComputedRef<boolean>
    balances: ComputedRef<boolean>
    diseases: ComputedRef<boolean>
  },
  subscriptions: ComputedRef<ClientSubscription[]> = computed(() => []),
): AlignedClientStreams {
  const { t } = useI18n()
  const { types: subscriptionTypes } = useSubscriptionTypeCatalog()
  const paymentsEnabled = computed(() => clientId.value != null && permissions.payments.value)
  const servicesEnabled = computed(() => clientId.value != null && permissions.services.value)
  const balancesEnabled = computed(() => clientId.value != null && permissions.balances.value)
  const diseasesEnabled = computed(() => clientId.value != null && permissions.diseases.value)
  const anyStreamEnabled = computed(
    () =>
      paymentsEnabled.value ||
      servicesEnabled.value ||
      balancesEnabled.value ||
      diseasesEnabled.value,
  )

  const paymentsQuery = useInfiniteQuery<ClientPaymentsResponse>({
    queryKey: ['clientPayments', clientId],
    queryFn: ({ pageParam }) =>
      fetchClientPayments(clientId.value!, pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: paymentsEnabled,
    staleTime: clientCardPollingInterval,
    refetchInterval: clientCardPollingInterval,
    refetchIntervalInBackground: false,
  })

  const servicesQuery = useInfiniteQuery<ClientBusinessServicesResponse>({
    queryKey: ['clientBusinessServices', clientId],
    queryFn: ({ pageParam }) =>
      fetchClientBusinessServices(clientId.value!, pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: servicesEnabled,
    staleTime: clientCardPollingInterval,
    refetchInterval: clientCardPollingInterval,
    refetchIntervalInBackground: false,
  })

  const balancesQuery = useInfiniteQuery<ClientBalancesResponse>({
    queryKey: ['clientBalances', clientId],
    queryFn: ({ pageParam }) =>
      fetchClientBalances(clientId.value!, pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: balancesEnabled,
    staleTime: clientCardPollingInterval,
    refetchInterval: clientCardPollingInterval,
    refetchIntervalInBackground: false,
  })

  const diseasesQuery = useInfiniteQuery<ClientDiseasesResponse>({
    queryKey: ['clientDiseases', clientId],
    queryFn: ({ pageParam }) =>
      fetchClientDiseases(clientId.value!, pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: diseasesEnabled,
    staleTime: clientCardPollingInterval,
    refetchInterval: clientCardPollingInterval,
    refetchIntervalInBackground: false,
  })

  const paymentsItems = computed<ClientPayment[]>(() => {
    const pages = paymentsQuery.data.value?.pages
    if (!pages?.length) return []
    return pages.flatMap((p) => p.items ?? [])
  })
  const servicesItems = computed<ClientBusinessService[]>(() => {
    const pages = servicesQuery.data.value?.pages
    if (!pages?.length) return []
    return pages.flatMap((p) => p.items ?? [])
  })
  const balancesItems = computed<ClientBalance[]>(() => {
    const pages = balancesQuery.data.value?.pages
    if (!pages?.length) return []
    return pages.flatMap((p) => p.items ?? [])
  })
  const diseasesItems = computed<ClientDisease[]>(() => {
    const pages = diseasesQuery.data.value?.pages
    if (!pages?.length) return []
    return pages.flatMap((p) => p.items ?? [])
  })

  const oldestPayment = computed(() => pickOldest(paymentsItems.value.map((i) => i.date)))
  const oldestService = computed(() => pickOldest(servicesItems.value.map((i) => i.date_time)))
  const oldestBalance = computed(() =>
    pickOldest(balancesItems.value.map((i) => i.calculation_date)),
  )
  const oldestDisease = computed(() => pickOldest(diseasesItems.value.map((i) => i.to_date)))

  const streamMetas = computed<StreamMeta[]>(() => [
    {
      name: 'payments',
      oldest: oldestPayment.value,
      hasNextPage: paymentsQuery.hasNextPage.value === true,
      isFetchingNextPage: paymentsQuery.isFetchingNextPage.value === true,
      fetchNextPage: () => {
        paymentsQuery.fetchNextPage()
      },
    },
    {
      name: 'services',
      oldest: oldestService.value,
      hasNextPage: servicesQuery.hasNextPage.value === true,
      isFetchingNextPage: servicesQuery.isFetchingNextPage.value === true,
      fetchNextPage: () => {
        servicesQuery.fetchNextPage()
      },
    },
    {
      name: 'balances',
      oldest: oldestBalance.value,
      hasNextPage: balancesQuery.hasNextPage.value === true,
      isFetchingNextPage: balancesQuery.isFetchingNextPage.value === true,
      fetchNextPage: () => {
        balancesQuery.fetchNextPage()
      },
    },
    {
      name: 'diseases',
      oldest: oldestDisease.value,
      hasNextPage: diseasesQuery.hasNextPage.value === true,
      isFetchingNextPage: diseasesQuery.isFetchingNextPage.value === true,
      fetchNextPage: () => {
        diseasesQuery.fetchNextPage()
      },
    },
  ])

  /**
   * Identifies streams that should fetch their next page right now to catch
   * up to the rest. The alignment target is the oldest date any stream has
   * reached so far; streams with a more recent `oldestFetched` and remaining
   * pages are the ones that lag and need to load more. Streams that are
   * already at or below the target, exhausted (`hasNextPage === false`),
   * already in flight, or yet to receive their first page are skipped.
   */
  function streamsToCatchUp(): StreamMeta[] {
    const metas = streamMetas.value
    const oldestDates = metas.map((m) => m.oldest).filter((d): d is string => typeof d === 'string')
    if (oldestDates.length === 0) return []
    const minOldest = oldestDates.reduce((a, b) => (a < b ? a : b))
    return metas.filter(
      (m) =>
        m.hasNextPage &&
        !m.isFetchingNextPage &&
        typeof m.oldest === 'string' &&
        m.oldest > minOldest,
    )
  }

  watch(
    streamMetas,
    () => {
      if (!anyStreamEnabled.value) return
      for (const m of streamsToCatchUp()) {
        if (import.meta.env.DEV) {
          console.log(`[useAlignedClientStreams] catching up "${m.name}" (oldest=${m.oldest})`)
        }
        m.fetchNextPage()
      }
    },
    { flush: 'post', immediate: true },
  )

  /**
   * Loads the next page of the bottleneck stream — the one whose
   * `oldestFetched` is the *newest* among streams with `hasNextPage`. That is
   * the stream which has reached the smallest distance back, so loading its
   * next page extends the merged window the least (and the watcher above will
   * then catch any other still-laggy streams up). When called while alignment
   * is active or no stream has more pages, the call is a no-op.
   */
  function extendBack() {
    const candidates = streamMetas.value.filter(
      (m) => m.hasNextPage && !m.isFetchingNextPage && typeof m.oldest === 'string',
    )
    if (candidates.length === 0) return
    let chosen = candidates[0]!
    for (const c of candidates) {
      if ((c.oldest as string) > (chosen.oldest as string)) chosen = c
    }
    chosen.fetchNextPage()
  }

  /*
   * Only weighs a stream's `.isPending` when that stream is actually
   * enabled — a permission-disabled TanStack Query never fetches, so it
   * stays `isPending: true` indefinitely, which would otherwise make this
   * permanently `true` the moment any single stream is disabled.
   */
  const isInitialLoading = computed(
    () =>
      (paymentsEnabled.value && paymentsQuery.isPending.value) ||
      (servicesEnabled.value && servicesQuery.isPending.value) ||
      (balancesEnabled.value && balancesQuery.isPending.value) ||
      (diseasesEnabled.value && diseasesQuery.isPending.value),
  )

  const isAligning = computed(() => streamsToCatchUp().length > 0)

  const isFetchingMore = computed(
    () =>
      paymentsQuery.isFetchingNextPage.value === true ||
      servicesQuery.isFetchingNextPage.value === true ||
      balancesQuery.isFetchingNextPage.value === true ||
      diseasesQuery.isFetchingNextPage.value === true,
  )

  const hasMore = computed(() => streamMetas.value.some((m) => m.hasNextPage))

  /**
   * Marks covered lessons with their ordinal inside the subscription (`3/5`)
   * and appends the "exhausted" / "expired" bands.
   */
  /**
   * Order of rows sharing one timestamp, oldest first: a payment precedes the
   * visit it paid for, and a note directly precedes its own row (so it lands
   * right below it once the ledger is reversed).
   */
  function tieKey(row: MergedRow, position: Map<MergedRow, number>): [number, number, number] {
    const target = row.anchor ?? row
    const group = target.kind === 'payment' ? 0 : target.kind === 'lesson' ? 1 : 2
    return [group, position.get(target) ?? 0, row.anchor ? 0 : 1]
  }

  function addSubscriptionMarks(out: MergedRow[]): void {
    const subs = subscriptions.value
    const bySub = new Map(subs.map((s) => [s.id, s]))

    const coveredBySub = new Map<string, MergedRow[]>()
    for (const row of out) {
      if (row.kind !== 'lesson' || !row.subscriptionId) continue
      const list = coveredBySub.get(row.subscriptionId) ?? []
      list.push(row)
      coveredBySub.set(row.subscriptionId, list)
    }
    /* Without `subscriptions_view` the client's subscription list is not
     * loaded: take the quota from the plan on the buying payment and number
     * the loaded visits oldest-first instead. */
    const typeIdBySub = new Map<string, string>()
    for (const row of out) {
      if (row.kind === 'payment' && row.subscriptionId && row.subscriptionTypeId) {
        typeIdBySub.set(row.subscriptionId, row.subscriptionTypeId)
      }
    }
    for (const [id, rows] of coveredBySub) {
      const sub = bySub.get(id)
      rows.sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
      if (sub) {
        let n = sub.used
        for (const row of rows) {
          row.subscriptionTotal = sub.total
          if (n === null || n < 1) continue
          row.subscriptionUsed = n
          n -= 1
        }
        continue
      }
      const typeId = typeIdBySub.get(id)
      const total = subscriptionTypes.value.find((x) => x.id === typeId)?.number_of_services ?? null
      let n = rows.length
      for (const row of rows) {
        row.subscriptionTotal = total
        if (total !== null) row.subscriptionUsed = n
        n -= 1
      }
    }

    /* Full-width "Subscription 3/5" note under each covered visit; shares the
     * visit's date and `anchor`, so after the ledger is reversed it lands
     * right below it. */
    for (const rows of coveredBySub.values()) {
      for (const row of rows) {
        out.push({
          kind: 'lesson-subscription',
          rowId: `lesson-subscription-${row.rowId}`,
          date: row.date,
          anchor: row,
          subscriptionId: row.subscriptionId,
          subscriptionUsed: row.subscriptionUsed,
          subscriptionTotal: row.subscriptionTotal,
        })
      }
    }

    let oldestLoaded = Infinity
    for (const row of out) {
      if (row.kind === 'lesson' || row.kind === 'payment') {
        oldestLoaded = Math.min(oldestLoaded, Date.parse(row.date))
      }
    }
    const now = Date.now()
    for (const sub of subs) {
      const covered = coveredBySub.get(sub.id) ?? []
      const exhausted = sub.isExhausted || (sub.total !== null && (sub.used ?? 0) >= sub.total)
      let when: number | null = null
      let kind: 'exhausted' | 'expired' | null = null
      if (exhausted && covered.length > 0) {
        // Just after the visit that used the last slot, so the band sits above it.
        when = Date.parse(covered[0]!.date) + 1000
        kind = 'exhausted'
      } else if (!exhausted && sub.validUntil && Date.parse(sub.validUntil) < now) {
        when = Date.parse(sub.validUntil)
        kind = 'expired'
      }
      if (when === null || kind === null || when < oldestLoaded) continue
      const chargeMinor = sub.price ? majorToMinor(sub.price) : undefined
      out.push({
        kind: 'subscription-event',
        rowId: `subscription-${sub.id}-${kind}`,
        date: new Date(when).toISOString(),
        subscriptionId: sub.id,
        subscriptionEvent: kind,
        subscriptionName: sub.name,
        subscriptionUsed: sub.used ?? undefined,
        subscriptionTotal: sub.total,
        // The plan's catalog price leaves the balance when the band is reached.
        deltaMinor: chargeMinor !== undefined ? -chargeMinor : undefined,
      })
    }
  }

  const rows = computed<MergedRow[]>(() => {
    if (isInitialLoading.value) return []
    const out: MergedRow[] = []

    /* Visit time has minute precision, so two visits of one type can share a
     * timestamp; the repeats get a `#n` suffix to keep row keys unique. */
    const lessonIdCount = new Map<string, number>()
    for (const s of servicesItems.value) {
      const baseId = `lesson-${s.business_service_type_id}-${s.date_time}`
      const seen = (lessonIdCount.get(baseId) ?? 0) + 1
      lessonIdCount.set(baseId, seen)
      const chargedMinor = majorToMinor(s.price_with_discount)
      const baseMinor = majorToMinor(s.base_price)
      out.push({
        kind: 'lesson',
        rowId: seen === 1 ? baseId : `${baseId}#${seen}`,
        date: s.date_time,
        /* Fallback string only — `ClientCard` resolves the real label via
         * `useServiceTypesStore` and reads `serviceTypeId` below. The
         * fallback is intentionally still emitted so the row has a non-empty
         * label during the brief window before the catalogue store has
         * loaded (and as a safety net for ids not present in the catalogue
         * at all, e.g. soft-deleted types). */
        label: t('clientCard.serviceFallback', { id: s.business_service_type_id }),
        serviceTypeId: s.business_service_type_id,
        subscriptionId: s.subscription_id ?? undefined,
        // `|| 0` turns a covered visit's `-0` into a plain `0` (no "-0,00").
        deltaMinor: chargedMinor !== undefined ? -chargedMinor || 0 : undefined,
        // A subscription-covered visit shows a plain 0, not a struck-through list price.
        basePriceMinor:
          baseMinor !== undefined && baseMinor !== chargedMinor && !s.subscription_id
            ? -baseMinor
            : undefined,
      })
    }
    for (const p of paymentsItems.value) {
      const paymentRow: MergedRow = {
        kind: 'payment',
        rowId: `payment-${p.id}`,
        date: p.date,
        label: t('cards.payment.paymentLabel'),
        subscriptionId: p.subscription_id ?? undefined,
        subscriptionTypeId: p.subscription_type_id ?? undefined,
        deltaMinor: majorToMinor(p.amount),
      }
      out.push(paymentRow)
      if (p.subscription_type_id) {
        /* Note row shown right under the payment: same date and `anchor`, so it
         * lands below the payment once the ledger is reversed. */
        out.push({
          kind: 'payment-subscription',
          rowId: `payment-subscription-${p.id}`,
          date: p.date,
          anchor: paymentRow,
          subscriptionTypeId: p.subscription_type_id,
        })
      }
    }
    for (const b of balancesItems.value) {
      out.push({
        kind: 'balance-event',
        rowId: `balance-${b.id}`,
        date: b.calculation_date,
        balanceAmountMinor: majorToMinor(b.amount),
      })
    }
    for (const d of diseasesItems.value) {
      out.push({
        kind: 'disease-event',
        rowId: `disease-${d.id}`,
        date: d.to_date,
        diseaseFromDate: d.from_date,
        diseaseNotes: d.notes,
      })
    }

    addSubscriptionMarks(out)

    /*
     * Walk forward in time to compute running balances. A `balance-event`
     * resets the running total to its snapshot amount; subsequent lessons
     * and payments adjust it.
     *
     * The seed value is `0`: rows that occur before the first encountered
     * monthly snapshot accumulate from a zero baseline, so the oldest
     * transactions in the loaded window render a sensible balance instead
     * of the muted placeholder. The first snapshot encountered moving
     * forward in time then re-anchors the running total to the
     * server-truth value, so any drift accumulated from `0` is corrected
     * the moment a snapshot row enters the walk.
     *
     * The walk uses every loaded `balance-event` regardless of whether
     * the UI ultimately renders it; that is why the display-side filter
     * below runs *after* this loop. Removing a balance row for the user
     * does not change the running totals on the surrounding transactions.
     */
    const position = new Map(out.map((r, i) => [r, i]))
    out.sort((a, b) => {
      const byTime = Date.parse(a.date) - Date.parse(b.date)
      if (byTime !== 0) return byTime
      const ka = tieKey(a, position)
      const kb = tieKey(b, position)
      return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2]
    })
    let running = 0
    for (const row of out) {
      if (row.kind === 'balance-event') {
        if (typeof row.balanceAmountMinor === 'number') {
          running = row.balanceAmountMinor
        }
        continue
      }
      if (row.kind === 'subscription-event' && typeof row.deltaMinor === 'number') {
        running += row.deltaMinor
        continue
      }
      if (
        row.kind === 'disease-event' ||
        row.kind === 'subscription-event' ||
        row.kind === 'payment-subscription' ||
        row.kind === 'lesson-subscription'
      )
        continue
      if (typeof row.deltaMinor !== 'number') continue
      running += row.deltaMinor
      row.balanceMinor = running
    }

    /*
     * Drop monthly-balance rows that did not see any activity since the
     * previously kept balance row. The backend emits a snapshot on the 1st
     * of every month even when the client has been inactive, which produces
     * long stretches of "Balance — 0.00" rows in the ledger for clients on
     * a hiatus. The user only finds those informative when the balance
     * could plausibly have changed since the last snapshot — i.e. when a
     * lesson or payment occurred in between — so we suppress every
     * balance row that is not "earned" by such activity.
     *
     * Walking ASC keeps the rule monotonic: `txnSinceLastBalance` is reset
     * each time we keep a balance row, then flipped back on by any
     * intervening lesson or payment. The very first balance row in the
     * loaded window is also subject to the rule (it is dropped unless a
     * transaction precedes it within the loaded window) — without that, a
     * client with no recent activity would still show a single anchor row
     * with the same 0.00 value the user asked us to hide.
     *
     * `disease-event` rows pass through untouched: they are informational
     * markers that do not affect the balance and should not promote a
     * subsequent balance row to "kept" on their own.
     */
    const visible: MergedRow[] = []
    let txnSinceLastBalance = false
    for (const row of out) {
      if (row.kind === 'balance-event') {
        if (txnSinceLastBalance) {
          visible.push(row)
          txnSinceLastBalance = false
        }
        continue
      }
      if (row.kind === 'lesson' || row.kind === 'payment') {
        txnSinceLastBalance = true
      }
      visible.push(row)
    }

    /* Display ordering is DESC (newest first) per the mock and the API. */
    visible.reverse()
    return visible
  })

  return {
    rows,
    isInitialLoading,
    isAligning,
    hasMore,
    isFetchingMore,
    extendBack,
  }
}
